import React, { useState } from 'react';
import type { FormFieldProps } from 'semantic-ui-react';

import type { IconName } from '../../element/Icon/icon.types';
import { IconNames } from '../../element/Icon/icon.types';
import { IconButton } from '../../element/Button/IconButton';
import { FormField } from '../FormField/FormField';
import { useFormContext } from '../Form/useFormContext';

import type { RawInputProps, RawInputValueType } from './RawInput';
import { RawInput } from './RawInput';

export type InputProps = {
  changeOnBlur?: boolean;
  description?: string;
  disabled?: RawInputProps['disabled'];
  icon?: IconName;
  id?: RawInputProps['id'];
  label?: React.ReactNode;
  name: RawInputProps['name'];
  onBlur?: (value?: RawInputValueType) => void;
  onChange?: RawInputProps['onChange'];
  onFocus?: (value?: RawInputValueType) => void;
  placeholder?: RawInputProps['placeholder'];
  Tooltip?: React.ReactNode;
};

const noopFn = () => {};
export const Input = ({
  changeOnBlur,
  description,
  disabled,
  icon,
  id,
  label,
  name,
  onBlur = noopFn,
  onChange = noopFn,
  onFocus = noopFn,
  placeholder,
  Tooltip,
}: InputProps) => {
  const inputId = id ?? name;
  const schemaPropertyType = useFormContext().getSchemaProperty(name)
    .type as RawInputProps['type'];
  const [displayAsPasswordType, setDisplayAsPasswordType] = useState(
    schemaPropertyType === 'SchemaPassword'
  );

  const getAppendButton = () => {
    switch (schemaPropertyType) {
      case 'SchemaPassword': {
        return (
          <IconButton
            pattern="secondary"
            variation="minimal"
            icon={displayAsPasswordType ? IconNames.eye : IconNames.eyeHidden}
            name="form-element-show-password"
            className="control-icon control-icon-hide-show-password fixed-dimension-inline"
            onClick={e => {
              e.stopPropagation();
              setDisplayAsPasswordType(!displayAsPasswordType);
            }}
          />
        );
      }
      default: {
        return null;
      }
    }
  };

  return (
    <FormField
      description={description}
      disabled={disabled}
      icon={icon}
      id={inputId}
      name={name}
      label={label}
      onClear={async (v: RawInputValueType) => onChange(v)}
      Tooltip={Tooltip}
      AppendButton={getAppendButton()}
      renderField={({
        value,
        setFieldValue,
        onBlur: formOnBlur,
        invalid,
      }: {
        value: RawInputProps['value'];
      } & FormFieldProps) => {
        return (
          <RawInput
            disabled={disabled}
            id={inputId}
            invalid={invalid}
            name={name}
            onBlur={(v, e) => {
              onBlur(v);
              formOnBlur(e);
              if (changeOnBlur) {
                void onChange(v);
                setFieldValue(name, v);
              }
            }}
            onChange={v => {
              if (!changeOnBlur) {
                void onChange(v);
                setFieldValue(name, v);
              }
            }}
            onFocus={v => {
              onFocus(v);
            }}
            placeholder={placeholder}
            type={
              !displayAsPasswordType && schemaPropertyType === 'SchemaPassword'
                ? 'SchemaString'
                : schemaPropertyType
            }
            value={value}
          />
        );
      }}
    />
  );
};
